import React from 'react';
import { FaCheck, FaLock, FaTrash } from 'react-icons/fa';

export function VotingCard({ voting, currentUser, onVote, onClose, onDelete }) {
  const totalVotes = voting.total_votes;
  const isCreator = voting.created_by === currentUser?.user_id;
  const hasVoted = voting.user_has_voted;
  const isClosed = voting.status === 'closed';

  // Calculate winner(s) for closed votings
  const getWinner = () => {
    if (!isClosed || voting.options.length === 0) return null;
    const maxVotes = Math.max(...voting.options.map(o => o.vote_count));
    return voting.options.filter(o => o.vote_count === maxVotes);
  };

  const winners = getWinner();

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition p-6 ${isClosed ? 'opacity-75' : ''}`}
      data-testid={`voting-card-${voting.voting_id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="text-lg font-bold text-gray-800">{voting.title}</h4>
          {voting.description && (
            <p className="text-sm text-gray-600 mt-1">{voting.description}</p>
          )}
        </div>
        <span className={`px-2 py-1 text-xs rounded-full ${
          isClosed ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
        }`}>
          {isClosed ? 'Closed' : 'Active'}
        </span>
      </div>

      {/* Results */}
      <div className="space-y-2 my-4">
        {voting.options.map((opt) => {
          const percentage = totalVotes > 0 ? Math.round((opt.vote_count / totalVotes) * 100) : 0;
          const isWinner = winners?.some(w => w.option_id === opt.option_id);
          const isUserVote = voting.user_vote?.includes(opt.option_id);

          return (
            <div key={opt.option_id} className="relative">
              <div className="flex justify-between text-sm mb-1">
                <span className={`${isWinner ? 'font-bold text-green-700' : 'text-gray-700'} ${isUserVote ? 'flex items-center' : ''}`}>
                  {opt.text}
                  {isUserVote && <FaCheck className="ml-2 text-blue-600" />}
                  {isWinner && isClosed && <span className="ml-2 text-xs">👑</span>}
                </span>
                <span className="text-gray-500">{opt.vote_count} ({percentage}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${isWinner && isClosed ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 mt-4 pt-4 border-t">
        <span>{totalVotes} / {voting.total_eligible_voters} voted</span>
        <span>by {voting.created_by_name}</span>
      </div>

      {/* Actions */}
      {!isClosed && (
        <div className="flex space-x-2 mt-4">
          {!hasVoted && (
            <button
              onClick={onVote}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
              data-testid={`vote-btn-${voting.voting_id}`}
            >
              Cast Your Vote
            </button>
          )}
          {hasVoted && (
            <span className="flex-1 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-center font-medium border-2 border-green-200 animate-pulse-once">
              <FaCheck className="inline mr-2" /> You've voted
            </span>
          )}
          {isCreator && (
            <button
              onClick={onClose}
              className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-xs sm:text-sm"
              title="Close voting"
            >
              <FaLock />
              <span className="sm:hidden">Close</span>
            </button>
          )}
        </div>
      )}

      {isClosed && isCreator && (
        <button
          onClick={onDelete}
          className="w-full mt-4 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center"
        >
          <FaTrash className="mr-2" /> Delete
        </button>
      )}
    </div>
  );
}
